import React, { useCallback, useEffect, useState } from 'react';
import aiDocumentService, { AiDocument } from '../services/aiDocumentService';
import EditableMarkdown from './EditableMarkdown';
import { useConfirm, RelatedLinks, type RelatedLinkItem } from './ui';
import { gtmPfad, type GtmTab } from '../vertrieb/gtmLinks';

/** Woraus das Dokument entstand: ICP, Persona, Offer aus den Eingaben. */
function herkunft(doc: AiDocument): RelatedLinkItem[] {
  const p = (doc.inputParams ?? {}) as Record<string, unknown>;
  const felder: Array<[string, GtmTab, string, 'target' | 'user' | 'briefcase']> = [
    ['icpId', 'icp', 'Zielkundenprofil', 'target'],
    ['personaId', 'persona', 'Persona', 'user'],
    ['offerId', 'offer', 'Angebot', 'briefcase'],
  ];
  return felder
    .filter(([feld]) => typeof p[feld] === 'string' && p[feld])
    .map(([feld, tab, label, icon]) => ({ key: feld, label, icon, to: gtmPfad(tab, p[feld] as string), title: `${label} im GTM-Setup öffnen` }));
}

interface SavedAiDocumentsProps {
  feature: string;
  // Bump this to force a reload (e.g. after a new generation was persisted).
  reloadKey?: number;
  title?: string;
}

/**
 * Collapsible list of saved AI documents for a feature, with inline edit/save
 * (via EditableMarkdown) and delete. Drop it onto any AI page so manually
 * corrected, persisted output is reloadable across sessions.
 */
const SavedAiDocuments: React.FC<SavedAiDocumentsProps> = ({
  feature,
  reloadKey,
  title = 'Gespeicherte Dokumente',
}) => {
  const { confirm } = useConfirm();
  const [docs, setDocs] = useState<AiDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setDocs(await aiDocumentService.list(feature));
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Fehler beim Laden der Dokumente');
    } finally {
      setLoading(false);
    }
  }, [feature]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const handleSave = async (id: string, content: string) => {
    await aiDocumentService.update(id, { content });
    setDocs((prev) => prev.map((d) => (d.id === id ? { ...d, content, isEdited: true } : d)));
  };

  const handleDelete = async (doc: AiDocument) => {
    const ok = await confirm({
      title: `„${doc.title}“ löschen?`,
      message: 'Das gespeicherte Dokument wird samt manueller Änderungen dauerhaft gelöscht. Rückgängig nur durch erneutes Generieren und Speichern.',
      confirmLabel: 'Löschen',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await aiDocumentService.delete(doc.id);
      setDocs((prev) => prev.filter((d) => d.id !== doc.id));
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Fehler beim Löschen');
    }
  };

  return (
    <div style={{ marginTop: '24px' }}>
      <h2 className="form-section-title">
        {title} ({docs.length})
      </h2>
      {error && <div className="alert alert-error">{error}</div>}
      {loading ? (
        <p style={{ color: 'var(--sec-fg-muted)' }}>Laden…</p>
      ) : docs.length === 0 ? (
        <div className="empty-state">
          <p className="empty-state-description">Noch keine gespeicherten Dokumente.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {docs.map((doc) => (
            <div key={doc.id} className="card">
              <div
                className="card-header"
                style={{ cursor: 'pointer' }}
                onClick={() => setExpandedId(expandedId === doc.id ? null : doc.id)}
              >
                <div>
                  <h3 className="card-title">
                    {doc.title}
                    {doc.isEdited && (
                      <span style={{ fontSize: '11px', color: 'var(--sec-info)', marginLeft: 8 }}>bearbeitet</span>
                    )}
                  </h3>
                  <p style={{ fontSize: '12px', color: 'var(--sec-fg-subtle)', marginTop: '4px' }}>
                    {new Date(doc.createdAt).toLocaleString('de-DE')}
                  </p>
                  {herkunft(doc).length > 0 && (
                    <div className="mt-1" onClick={(e) => e.stopPropagation()}>
                      <RelatedLinks items={herkunft(doc)} label={`Grundlage von ${doc.title}`} />
                    </div>
                  )}
                </div>
                <span style={{ fontSize: '18px', color: 'var(--sec-fg-subtle)' }}>
                  {expandedId === doc.id ? '▲' : '▼'}
                </span>
              </div>
              {expandedId === doc.id && (
                <>
                  <EditableMarkdown content={doc.content} onSave={(c) => handleSave(doc.id, c)} />
                  <div className="card-footer">
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(doc)}>
                      Löschen
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SavedAiDocuments;
